import { cloneElement, Fragment, type ReactElement, type ReactNode, useMemo } from 'react';
import type { I18nKey, I18nNamespace, I18nScopedKey, I18nTranslateOptions } from './types';
import { useTranslation } from './useTranslation';

type ParsedTextNode = {
  type: 'text';
  value: string;
};

type ParsedTagNode = {
  type: 'tag';
  name: string;
  children: ParsedNode[];
};

type ParsedNode = ParsedTextNode | ParsedTagNode;

const TAG_PATTERN = /<\/?([A-Za-z][A-Za-z0-9_-]*)>/g;
const getOpeningTag = (name: string) => `<${name}>`;
const getClosingTag = (name: string) => `</${name}>`;

const parseTransTemplate = (template: string): ParsedNode[] => {
  const root: ParsedTagNode = {
    children: [],
    name: 'root',
    type: 'tag',
  };
  const stack: ParsedTagNode[] = [root];
  let cursor = 0;

  TAG_PATTERN.lastIndex = 0;
  let match = TAG_PATTERN.exec(template);

  while (match) {
    const [fullTag, tagName] = match;
    const matchIndex = match.index;
    const isClosingTag = fullTag.startsWith('</');
    const currentNode = stack[stack.length - 1];

    if (matchIndex > cursor) {
      currentNode.children.push({
        type: 'text',
        value: template.slice(cursor, matchIndex),
      });
    }

    if (isClosingTag) {
      if (stack.length > 1 && currentNode.name === tagName) {
        stack.pop();
      } else {
        currentNode.children.push({
          type: 'text',
          value: fullTag,
        });
      }
    } else {
      const tagNode: ParsedTagNode = {
        children: [],
        name: tagName,
        type: 'tag',
      };
      currentNode.children.push(tagNode);
      stack.push(tagNode);
    }

    cursor = matchIndex + fullTag.length;
    match = TAG_PATTERN.exec(template);
  }

  if (cursor < template.length) {
    stack[stack.length - 1].children.push({
      type: 'text',
      value: template.slice(cursor),
    });
  }

  return root.children;
};

const renderParsedNodes = (
  nodes: ParsedNode[],
  components: Record<string, ReactElement>,
  keyPrefix = 'trans'
): ReactNode[] => {
  return nodes.map((node, index) => {
    const key = `${keyPrefix}.${index}`;

    if (node.type === 'text') {
      return <Fragment key={key}>{node.value}</Fragment>;
    }

    const renderedChildren = renderParsedNodes(node.children, components, key);
    const component = components[node.name];

    if (!component) {
      return (
        <Fragment key={key}>
          {getOpeningTag(node.name)}
          {renderedChildren}
          {getClosingTag(node.name)}
        </Fragment>
      );
    }

    return cloneElement(component, { key }, renderedChildren);
  });
};

type TransProps = {
  components?: Record<string, ReactElement>;
  defaultValue?: string;
  values?: I18nTranslateOptions['values'];
};

type ScopedTransProps = {
  [Namespace in I18nNamespace]: TransProps & {
    i18nKey: I18nScopedKey<Namespace>;
    namespace: Namespace;
  };
}[I18nNamespace];

type UnscopedTransProps = TransProps & {
  i18nKey: I18nKey;
  namespace?: undefined;
};

type TransComponent = {
  (props: ScopedTransProps): ReactNode;
  (props: UnscopedTransProps): ReactNode;
};

export const Trans: TransComponent = ({
  i18nKey,
  namespace,
  components = {},
  defaultValue,
  values,
}: {
  i18nKey: string;
  namespace?: I18nNamespace;
  components?: Record<string, ReactElement>;
  defaultValue?: string;
  values?: Record<string, string | number | boolean | null | undefined>;
}): ReactNode => {
  const { t } = useTranslation(namespace as never);
  const template = (t as (key: string, options?: I18nTranslateOptions) => string)(i18nKey, { defaultValue, values });
  const parsedTemplate = useMemo(() => parseTransTemplate(template), [template]);

  return <>{renderParsedNodes(parsedTemplate, components)}</>;
};
